import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import {
  Dialog,
  Stack,
  P,
  Button,
  Feedback,
  Checkbox,
} from '@fashionnovateam/nova-ui-toolkit';
import toast from '../Toast/Toast';

interface EmailUpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  oldEmail: string;
  newEmail: string;
  onConfirm: () => Promise<void>;
  userRole?: string;
}

export default function EmailUpdateDialog({
  isOpen,
  onClose,
  oldEmail,
  newEmail,
  onConfirm,
  userRole,
}: EmailUpdateDialogProps) {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await onConfirm();

      if (userRole === 'INFLUENCER') {
        toast(
          {
            title: 'Profile successfully updated',
            text: 'Check your email for your access code to sign back into your account.',
          },
          {
            type: 'success',
            duration: 5000,
          },
        );

        localStorage.clear();
        sessionStorage.clear();
        setShouldRedirect(true);
      } else {
        toast('Profile successfully updated', {
          type: 'success',
          duration: 5000,
        });
      }

      setIsConfirmed(false);
      onClose();
    } catch (error) {
      toast('Failed to update email. Please try again or contact support.', {
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setIsConfirmed(false);
  };

  if (shouldRedirect) {
    return <Redirect to="/influencer-login" />;
  }

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} className="max-w-xl">
      <Dialog.Header>
        <Dialog.Title />
      </Dialog.Header>
      <Dialog.Content>
        <Stack direction="col" gap="md">
          <P size="2xl" weight="medium">
            Confirm Your New Email
          </P>
          <Feedback
            title="You're about to change the primary email address on your account. After this, all two-factor authentication codes will go to your new email. You will be signed out automatically and will need to sign back in with your new email and existing password."
            type="warning"
            className="text-sm"
          />
        </Stack>
        <Stack direction="col" gap="md" className="mt-4">
          <Stack direction="col" gap="sm">
            <Stack direction="row" gap="sm">
              <P size="sm" color="disabled" className="w-20">
                Old Email
              </P>
              <P size="sm">{oldEmail}</P>
            </Stack>
            <Stack direction="row" gap="sm">
              <P size="sm" color="disabled" className="w-20">
                New Email
              </P>
              <P size="sm">{newEmail}</P>
            </Stack>
          </Stack>
          <Stack direction="row" gap="sm" align="center">
            <Checkbox
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              disabled={isLoading}
            />
            <P size="xsm" weight="semibold">
              I confirm my new email address is accurate and agree to this
              change
            </P>
          </Stack>
        </Stack>
      </Dialog.Content>
      <Dialog.Footer>
        <Stack direction="row" gap="sm" content="end">
          <Button color="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={!isConfirmed || isLoading}
            loading={isLoading}
          >
            Confirm
          </Button>
        </Stack>
      </Dialog.Footer>
    </Dialog>
  );
}
